import { NotificationProps } from '@abbyy/react-ui';
import {
    AlertsWarningFill,
    CheckFill,
    ClockOutline,
    CloudUploadFill,
    Divider,
    FlexContainer,
    MenuItem,
    Notification,
    Typography
} from 'components';
import { InputIcon } from 'components/icons';
import { useEventList } from 'providers/event-list';
import { EventDetails } from 'providers/event-list/types';
import { FC, PropsWithChildren, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';

type EventNotificationProps = Pick<EventDetails, 'isRead'> & NotificationProps;

const EventNotification: FC<PropsWithChildren<EventNotificationProps>> = ({
    children,
    isRead,
    completed,
    actionText,
    onActionClick,
    icon
}) => {
    return (
        <>
            <MenuItem>
                <Notification
                    isUnread={!isRead}
                    icon={icon}
                    completed={completed}
                    actionText={actionText}
                    onActionClick={onActionClick}>
                    {children}
                </Notification>
            </MenuItem>
            <Divider orientation="horizontal" spacing={1} />
        </>
    );
};

interface ProgressProps {
    progressPercent: number;
    progressPreFix?: string;
}

const Progress: FC<ProgressProps> = ({ progressPercent, progressPreFix }) => {
    const { t } = useTranslation();

    return (
        <FlexContainer>
            {t('general.Status')}: {progressPreFix} ({progressPercent}%)
        </FlexContainer>
    );
};

interface EventProps {
    details: EventDetails;
    workerId: string;
}

const UploadEvent = ({ details, workerId }: EventProps) => {
    const { t } = useTranslation();
    const { cancelUpload } = useEventList();
    const [isCancelling, setIsCancelling] = useState(false);
    const uploadingScreenshots =
        details.status === 'uploadUploadingScreenshots';

    return (
        <EventNotification
            isRead={details.isRead}
            completed={details.time}
            actionText={!isCancelling && t('general.Cancel')}
            onActionClick={() => {
                setIsCancelling(true);
                cancelUpload(workerId);
            }}>
            <FlexContainer>
                <Typography
                    variant="subtitle1"
                    weight="semi"
                    color={['grey', 800]}>
                    {t('expressions.uploading_logs')}
                </Typography>
            </FlexContainer>
            <FlexContainer>
                {t('expressions.project_name')}: {details.projectName}
            </FlexContainer>
            <FlexContainer>
                {t('general.Logs')}: {details.logList.join(', ')}
            </FlexContainer>
            {uploadingScreenshots ? (
                <Progress
                    progressPercent={details.progressPercent}
                    progressPreFix={t('expressions.uploading_screenshots')}
                />
            ) : (
                <Progress
                    progressPercent={details.progressPercent}
                    progressPreFix={t('expressions.uploading_logs')}
                />
            )}

            <FlexContainer>
                {t('expressions.size_in_megabytes')}: {details.jobSizeMegabytes}
            </FlexContainer>
            <FlexContainer>
                {t('expressions.logs_count')}: {details.logsCount}
            </FlexContainer>
        </EventNotification>
    );
};

const UploadEventFinished = ({ details }: EventProps) => {
    const { t } = useTranslation();

    return (
        <EventNotification
            isRead={!details.isRead}
            icon={<CheckFill color="success" />}
            completed={details.time}>
            <FlexContainer>
                <Typography
                    variant="subtitle1"
                    weight="semi"
                    color={['grey', 800]}>
                    {t('expressions.importing_success')}
                </Typography>
                <div>
                    <Trans
                        i18nKey="expressions.uploading_logs_success"
                        values={{
                            logList: details.logList.join(', '),
                            projectName: details.projectName
                        }}
                        components={[
                            // eslint-disable-next-line
                            <a
                                key="projectLink"
                                target="_blank"
                                rel="noreferrer"
                                href={details.projectUrl}
                            />
                        ]}
                    />
                </div>
            </FlexContainer>
        </EventNotification>
    );
};

const TransformationEventFinished = ({ details }: EventProps) => {
    const { t } = useTranslation();

    return (
        <EventNotification
            isRead={!details.isRead}
            icon={<CheckFill color="success" />}
            completed={details.time}>
            <FlexContainer>
                {details.logList.length
                    ? t('expressions.transformation_success', {
                          logList: details.logList.join(', ')
                      })
                    : t('expressions.transformation_success_all')}
            </FlexContainer>
        </EventNotification>
    );
};

const DeletionFinished = ({ details }: EventProps) => {
    const { t } = useTranslation();

    return (
        <EventNotification
            isRead={!details.isRead}
            icon={<CheckFill color="success" />}
            completed={details.time}>
            <FlexContainer>
                {t('expressions.deletion_success', {
                    logList: details.logList.join(', ')
                })}
            </FlexContainer>
        </EventNotification>
    );
};

const UploadEventProcessing = ({ details }: EventProps) => {
    const { t } = useTranslation();

    return (
        <EventNotification
            isRead={!details.isRead}
            icon={<InputIcon color="primary" />}
            completed={details.time}>
            <FlexContainer>
                <Typography
                    variant="subtitle1"
                    weight="semi"
                    color={['grey', 800]}>
                    {t('expressions.importing_logs')}
                </Typography>
                {t('expressions.uploading_logs_process', {
                    projectName: details.projectName,
                    logList: details.logList.join(', ')
                })}
            </FlexContainer>
        </EventNotification>
    );
};

const DeletionInProgress = ({ details, workerId }: EventProps) => {
    const { t } = useTranslation();
    const { cancelDeletion } = useEventList();
    const [isCancelling, setIsCancelling] = useState(false);

    return (
        <EventNotification
            isRead={!details.isRead}
            icon={<InputIcon color="primary" />}
            completed={details.time}
            actionText={!isCancelling && t('general.Cancel')}
            onActionClick={() => {
                setIsCancelling(true);
                cancelDeletion(workerId);
            }}>
            <FlexContainer>
                <Typography
                    variant="subtitle1"
                    weight="semi"
                    color={['grey', 800]}>
                    {t('expressions.deleting_logs', {
                        logList: details.logList.join(', ')
                    })}
                </Typography>
                <FlexContainer>
                    {t('general.Status')}: {t('general.In_progress')} (
                    {details.progressPercent}%)
                </FlexContainer>
            </FlexContainer>
        </EventNotification>
    );
};

const TransformationEventInProgress = ({ details }: EventProps) => {
    const { t } = useTranslation();

    return (
        <EventNotification
            isRead={!details.isRead}
            icon={<CloudUploadFill color="primary" />}
            completed={details.time}>
            <FlexContainer>
                {details.logList.length
                    ? t('expressions.transforming_logs', {
                          logList: details.logList.join(', ')
                      })
                    : t('expressions.transforming_logs_all')}
            </FlexContainer>
            <Progress
                progressPercent={details.progressPercent}
                progressPreFix={t('general.In_progress')}
            />
        </EventNotification>
    );
};

const UploadEventFailed = ({ details }: EventProps) => {
    const { t } = useTranslation();

    return (
        <EventNotification
            isRead={!details.isRead}
            icon={<AlertsWarningFill color="warning" />}
            completed={details.time}>
            <FlexContainer>
                <Typography
                    variant="subtitle1"
                    weight="semi"
                    color={['grey', 800]}>
                    {t('expressions.importing_failed')}
                </Typography>
                {t('expressions.uploading_logs_failed', {
                    projectName: details.projectName,
                    logList: details.logList.join(', ')
                })}
            </FlexContainer>
        </EventNotification>
    );
};

const TransformationEventFailed = ({ details }: EventProps) => {
    const { t } = useTranslation();

    return (
        <EventNotification
            isRead={!details.isRead}
            icon={<AlertsWarningFill color="warning" />}
            completed={details.time}>
            <FlexContainer>
                {details.logList.length
                    ? t('expressions.transformation_failed', {
                          logList: details.logList.join(', ')
                      })
                    : t('expressions.transformation_failed_all')}
            </FlexContainer>
        </EventNotification>
    );
};

const UploadEventCancelled = ({ details }: EventProps) => {
    const { t } = useTranslation();

    return (
        <EventNotification
            isRead={!details.isRead}
            icon={<AlertsWarningFill color="warning" />}
            completed={details.time}>
            <FlexContainer>
                {t('expressions.uploading_logs_cancelled', {
                    logList: details.logList.join(', ')
                })}
            </FlexContainer>
        </EventNotification>
    );
};

const TransformationEventCancelled = ({ details }: EventProps) => {
    const { t } = useTranslation();

    return (
        <EventNotification
            isRead={!details.isRead}
            icon={<AlertsWarningFill color="warning" />}
            completed={details.time}>
            <FlexContainer>
                {details.logList.length
                    ? t('expressions.transformation_cancelled', {
                          logList: details.logList.join(', ')
                      })
                    : t('expressions.transformation_cancelled_all')}
            </FlexContainer>
        </EventNotification>
    );
};

const DeletionCancelled = ({ details }: EventProps) => {
    const { t } = useTranslation();

    return (
        <EventNotification
            isRead={!details.isRead}
            icon={<AlertsWarningFill color="warning" />}
            completed={details.time}>
            <FlexContainer>
                {t('expressions.deletion_cancelled', {
                    logList: details.logList.join(', ')
                })}
            </FlexContainer>
        </EventNotification>
    );
};

const UploadEventQueued = ({ details, workerId }: EventProps) => {
    const { t } = useTranslation();
    const { cancelUpload } = useEventList();
    const [isCancelling, setIsCancelling] = useState(false);

    return (
        <EventNotification
            isRead={!details.isRead}
            icon={<ClockOutline color="primary" />}
            completed={details.time}
            actionText={!isCancelling && t('general.Cancel')}
            onActionClick={() => {
                setIsCancelling(true);
                cancelUpload(workerId);
            }}>
            <FlexContainer>
                <Typography
                    variant="subtitle1"
                    weight="semi"
                    color={['grey', 800]}>
                    {t('expressions.import_queued')}
                </Typography>
                {t('expressions.uploading_logs_queued', {
                    projectName: details.projectName,
                    logList: details.logList.join(', ')
                })}
            </FlexContainer>
        </EventNotification>
    );
};

const TransformationEventQueued = ({ details, workerId }: EventProps) => {
    const { t } = useTranslation();
    const { cancelTransformation } = useEventList();
    const [isCancelling, setIsCancelling] = useState(false);

    return (
        <EventNotification
            isRead={!details.isRead}
            icon={<ClockOutline color="primary" />}
            completed={details.time}
            actionText={!isCancelling && t('general.Cancel')}
            onActionClick={() => {
                setIsCancelling(true);
                cancelTransformation(workerId);
            }}>
            <FlexContainer>
                {details.logList.length
                    ? t('expressions.transformation_queued', {
                          logList: details.logList.join(', ')
                      })
                    : t('expressions.transformation_queued_all')}
            </FlexContainer>
        </EventNotification>
    );
};

const DeletionQueued = ({ details, workerId }: EventProps) => {
    const { t } = useTranslation();
    const { cancelDeletion } = useEventList();
    const [isCancelling, setIsCancelling] = useState(false);

    return (
        <EventNotification
            isRead={!details.isRead}
            icon={<ClockOutline color="primary" />}
            completed={details.time}
            actionText={!isCancelling && t('general.Cancel')}
            onActionClick={() => {
                setIsCancelling(true);
                cancelDeletion(workerId);
            }}>
            <FlexContainer>
                {t('expressions.deletion_queued', {
                    logList: details.logList.join(', ')
                })}
            </FlexContainer>
        </EventNotification>
    );
};

const ObfuscationQueued = ({ details }: EventProps) => {
    const { t } = useTranslation();

    return (
        <EventNotification
            isRead={!details.isRead}
            icon={<ClockOutline color="primary" />}
            completed={details.time}>
            <FlexContainer>
                {t('expressions.obfuscation_queued', {
                    id: details.logList[0]
                })}
            </FlexContainer>
        </EventNotification>
    );
};

const ObfuscationPreProcessing = ({ details }: EventProps) => {
    const { t } = useTranslation();

    return (
        <EventNotification
            isRead={!details.isRead}
            icon={<ClockOutline color="primary" />}
            completed={details.time}>
            <FlexContainer>
                {t('expressions.obfuscation_pre_processing', {
                    id: details.logList[0]
                })}
            </FlexContainer>
            <Progress
                progressPercent={details.progressPercent}
                progressPreFix={t('general.In_progress')}
            />
        </EventNotification>
    );
};

const ObfuscationProcessing = ({ details }: EventProps) => {
    const { t } = useTranslation();

    return (
        <EventNotification
            isRead={!details.isRead}
            icon={<CloudUploadFill color="primary" />}
            completed={details.time}>
            <FlexContainer>
                {t('expressions.obfuscation_processing', {
                    id: details.logList[0]
                })}
            </FlexContainer>
            <FlexContainer>
                {t('general.Status')}: {t('general.In_progress')} (
                {details.progressPercent}%)
            </FlexContainer>
        </EventNotification>
    );
};

const ObfuscationFinished = ({ details }: EventProps) => {
    const { t } = useTranslation();

    return (
        <EventNotification
            isRead={!details.isRead}
            icon={<ClockOutline color="success" />}
            completed={details.time}>
            <FlexContainer>
                {t('expressions.obfuscation_finished', {
                    id: details.logList[0]
                })}
            </FlexContainer>
        </EventNotification>
    );
};

const ObfuscationFailed = ({ details }: EventProps) => {
    const { t } = useTranslation();

    return (
        <EventNotification
            isRead={!details.isRead}
            icon={<AlertsWarningFill color="warning" />}
            completed={details.time}>
            <FlexContainer>
                {t('expressions.obfuscation_failed', {
                    id: details.logList[0]
                })}
            </FlexContainer>
        </EventNotification>
    );
};

const DeletionFailed = ({ details }: EventProps) => {
    const { t } = useTranslation();

    return (
        <EventNotification
            isRead={!details.isRead}
            icon={<AlertsWarningFill color="warning" />}
            completed={details.time}>
            <FlexContainer>
                {t('expressions.odeletion_failed', {
                    ids: details.logList.join(', ')
                })}
            </FlexContainer>
        </EventNotification>
    );
};

export const notificationMap = new Map([
    ['uploadQueued', UploadEventQueued],
    ['transformationQueued', TransformationEventQueued],
    ['uploadUploadingLogs', UploadEvent],
    ['uploadUploadingScreenshots', UploadEvent],
    ['uploadFailed', UploadEventFailed],
    ['transformationFailed', TransformationEventFailed],
    ['uploadFinished', UploadEventFinished],
    ['uploadProcessing', UploadEventProcessing],
    ['uploadCancelled', UploadEventCancelled],
    ['transformationCancelled', TransformationEventCancelled],
    ['transformationFinished', TransformationEventFinished],
    ['transformationInProgress', TransformationEventInProgress],
    ['obfuscationQueued', ObfuscationQueued],
    ['obfuscationPreProcessing', ObfuscationPreProcessing],
    ['obfuscationProcessing', ObfuscationProcessing],
    ['obfuscationFinished', ObfuscationFinished],
    ['obfuscationFailed', ObfuscationFailed],
    ['deletionQueued', DeletionQueued],
    ['deletionInProgress', DeletionInProgress],
    ['deletionFinished', DeletionFinished],
    ['deletionFailed', DeletionFailed],
    ['deletionCancelled', DeletionCancelled]
]);
